import type { ReactNode } from 'react';

/**
 * Scientific notation in a bilingual interface (D328).
 *
 * Symbols, formulas, numbers and units are LEFT-TO-RIGHT in both languages —
 * `Mw = Mcms − Mcds` is read the same way on an Arabic page — while the words
 * around them stay right-to-left. Left alone, the browser's bidirectional
 * algorithm lets neighbouring Arabic text and punctuation pull such a run
 * apart: brackets flip, a unit drifts to the wrong side of its number, `=`
 * lands at the far end. Every piece here ISOLATES the run instead (`<bdi>` plus
 * `unicode-bidi: isolate` from `.sci-expr` in styles.css), so it is laid out on
 * its own and then placed as one unit in the surrounding text.
 *
 * Presentation only: nothing here computes, rounds or formats a number. The
 * caller passes the string its template's existing formatter already produced.
 */

/** LEFT-TO-RIGHT ISOLATE ... POP DIRECTIONAL ISOLATE: the text-only form. */
export const LTR_ISOLATE = '⁦';
export const POP_ISOLATE = '⁩';
/** Keeps a unit on the same line as its number. */
export const NO_BREAK_SPACE = ' ';

const ARABIC_SCRIPT = /[؀-ۿ]/;

/**
 * For a place that only takes TEXT (an aria-label, a title, a select option):
 * the run wrapped in invisible isolate marks.
 */
export function sciText(text: string): string {
  return `${LTR_ISOLATE}${text}${POP_ISOLATE}`;
}

/** A symbol or a formula, isolated left to right: `Mw`, `A / (B − C)`. */
export function SciExpr({ children, className }: { readonly children: ReactNode; readonly className?: string }) {
  return (
    <bdi dir="ltr" className={`sci-expr${className ? ` ${className}` : ''}`} data-sci="expr">
      {children}
    </bdi>
  );
}

/**
 * A formula line. Symbolic notation (`w = Mw / Ms × 100`) is isolated left to
 * right; a formula a template words in Arabic (`= مجموع كتل الأجزاء`) is
 * isolated in ITS OWN direction, so its `=` sits where an Arabic reader
 * expects it. Either way the run never mixes with what surrounds it.
 */
export function Formula({ children, className }: { readonly children: string; readonly className?: string }) {
  const worded = ARABIC_SCRIPT.test(children);
  return (
    <bdi
      dir={worded ? 'auto' : 'ltr'}
      className={`sci-expr measure text-micro text-calc-ink${className ? ` ${className}` : ''}`}
      data-sci="formula"
    >
      {children}
    </bdi>
  );
}

/**
 * A number with its unit, as one left-to-right isolate with a no-break space:
 * `524.0 cm³`, `19.7 %`, `2.309`. The value string is the template's own
 * formatted value; `null` is never turned into a number here.
 */
export function Quantity({
  value,
  unit,
  className,
}: {
  readonly value: string;
  readonly unit?: string | undefined;
  readonly className?: string;
}) {
  return (
    <bdi dir="ltr" className={`sci-expr measure${className ? ` ${className}` : ''}`} data-sci="quantity">
      {value}
      {unit ? `${NO_BREAK_SPACE}${unit}` : null}
    </bdi>
  );
}

/**
 * A label in words that CONTAINS notation — `كتلة الوعاء (Mc)، غ`,
 * `الوزن النوعي = A / (B − C)` — with every notation run isolated left to right
 * in text form, so brackets and operators cannot reorder against the Arabic.
 * English labels are returned untouched: there is nothing to isolate from.
 */
export function isolateNotation(label: string): string {
  if (!ARABIC_SCRIPT.test(label)) return label;
  return label.replace(/[A-Za-z0-9([][A-Za-z0-9\s=+\-−×÷/().,%³²°'"[\]]*/g, (run) => {
    if (!/[A-Za-z0-9]/.test(run)) return run;
    const trimmed = run.replace(/\s+$/, '');
    // A run must close every bracket it opens, or it would swallow Arabic.
    const balanced = (trimmed.match(/\(/g)?.length ?? 0) === (trimmed.match(/\)/g)?.length ?? 0);
    if (!balanced) return run;
    return `${sciText(trimmed)}${run.slice(trimmed.length)}`;
  });
}
